import { LoadingDots } from "PFComponents/loading_dots";
import { Select } from "PFComponents/select/select";
import EchartsWrapper from "PFCore/helpers/echarts_wrapper";
import useStorage from "PFCore/helpers/use_storage";
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import DateGranularity from "../../../common/date_granularity_selector";
import LinePreview from "../../../previews/line_preview";
import { utilizationChartConfig } from "./configs/utilization_cart_config";
import css from "./utilization_chart.module.scss";

type UtilizationChartProps = {
  utilizationData:
    | {
        date: string;
        total_utilisation: number;
        billable_utilisation: number;
      }[]
    | any;
  utilizationOption: string;
  loading: boolean;
  dateGranularity: string;
  setDateGranularity: Dispatch<SetStateAction<string>>;
  error: boolean;
};

const UtilizationChart = ({
  utilizationData,
  utilizationOption,
  loading,
  dateGranularity,
  setDateGranularity,
  error
}: UtilizationChartProps) => {
  const { t } = useTranslation("insights");
  const [goal, setGoal] = useStorage(`bookingInsightsGoal`, false);

  const [data, setData] = useState<number[]>([]);
  const [selectedValue, setSelectedValue] = useState<number>(goal || 50);

  const selectedAxis = {
    daily: "date",
    weekly: "week_start",
    monthly: "month_name",
    undefined: "date"
  }[dateGranularity];

  // @ts-ignore
  const xAxis = useMemo(() => utilizationData?.map((data) => data[selectedAxis]), [utilizationData]);

  useEffect(() => {
    if (utilizationData) {
      setData(
        utilizationOption === "total"
          ? utilizationData.map((data) => data.total_utilisation * 100)
          : utilizationData.map((data) => data.billable_utilisation * 100)
      );
    }
  }, [utilizationOption, utilizationData]);

  const selectOptions = useMemo(() => {
    const options: { id: number; item: number; displayElement: string }[] = [];

    for (let i = 0; i <= 100; i = i + 10) {
      options.push({
        id: i,
        displayElement: `${i}%`,
        item: i
      });
    }
    return options;
  }, []);

  const options = useMemo(
    () => utilizationChartConfig(xAxis, data, selectedValue, t("bookingReports.utilization")),
    [xAxis, data, selectedValue]
  );

  if (!data || error) {
    return <LinePreview content={t("noData")} />;
  }

  return (
    <div className={css.wrapper}>
      <div className={css.header}>
        <h3>{t(`bookingReports.${utilizationOption}Utilization` as unknown as TemplateStringsArray)}</h3>
        <div className={css.actionsWrapper}>
          <DateGranularity selectedDate={dateGranularity} setDateGranularity={setDateGranularity} />
          <Select
            /* @ts-ignore */
            label={t("bookingReports.target")}
            options={selectOptions}
            value={`${selectedValue}%`}
            controlledValue
            handleChange={(value) => {
              setGoal(value);
              setSelectedValue(value);
            }}
            kind="bordered"
          />
        </div>
      </div>
      {loading ? (
        <LoadingDots circlesEnabled pageViewEnabled />
      ) : (
        <EchartsWrapper options={options} height={600} />
      )}
    </div>
  );
};

export default UtilizationChart;
